import { produce } from "immer";
import { type Dispatch } from "redux";

import { logger } from "@jslib/shared/utils/logger";

import i18next, { logLanguageChangeErrors } from "@/lib/intl/i18next";

import {
  type DatabaseId,
  type VerificationResponse,
  type ViewModel,
  type BoundAction,
  type BoundActionCreator,
  type FieldValidationErrors,
  type VerificationStep,
  type ProgramTheme,
  type Locale,
  type Segment,
  type ProgramThemeMessages,
  type DocUploadViewModel,
  type SetMessagesAction,
  type ProgramThemeAction,
  type VerificationResponseAction,
  type ViewModelAction,
  type IsLoadingAction,
  type FieldValidationErrorsAction,
  type PreviousViewModelAction,
  type PersonalInfoViewModel,
  type Country,
  type WithCoreFields,
  type StringMap,
  type WithLocaleChoiceAndCountryChoice,
  type VerificationServiceAction,
  type RequiredMetadataAndExternalIdCheckAction,
} from "../types/types";
import {
  VerificationApiClient,
  getMockVerificationRequestErrorResponse,
} from "../ServerApi/VerificationApiClient";
import { hooks } from "../hooks";
import { getMessages, getLocaleSafely } from "../intl/intl";
import {
  getThemeMessages,
  getMetadataConfig,
  getConfiguredCountries,
} from "../ProgramTheme/programThemeGetters";
import { saveVerificationIdForConversion } from "../conversion/conversion";
import { scrollToWindowTop } from "../utils/browser/windowHelpers";
import { getFingerprint, getFingerprintRequestId } from "../fingerprint/fingerprintUtils";
import { getIpqsRequestId } from "../IPQS/IPQSutils";
import { getMetadataFromUrl } from "../metadata/metadata";
import { isCollectStep } from "../types/runtimeTypes";
import { getEmptyViewModel } from "../ServerApi/VerificationRequestGetters";
import { getFieldValidationErrorsEmpty, GetEmptyTheme } from "../types/empties";
import { closeTabRef } from "../refs/refs";
import { setGaDimensionTestRequest } from "../GoogleAnalytics/ga";
import { getQueryOrOption } from "../ServerApi/TestingRouteOverrides";
import { QUERY_STRING_EXTERNAL_USER_ID } from "../../constants";
import { getOptions, setOptions } from "../../options/options";
import { setProgramBaseline } from "../state/programStore";
import { getVerificationIdFromQueryString } from "../utils/routing/Url";

import {
  handleSubmitResponse,
  initViewModel,
  clearUploadedFiles,
  enhanceOrganizationFromTheme,
  determineCountry,
  getServerFieldValidationErrors,
} from "./VerificationServiceHelpers";
import { type ViewModelUpdateOptions } from "./ViewModel";

/**
 * @description Start a new verification "flow"
 * @param dispatch Redux action dispatch
 */
export const createFetchNewVerificationRequest =
  (dispatch: Dispatch) =>
  async <T extends ViewModel = ViewModel>(
    programId: DatabaseId,
    segment: Segment,
    previousViewModel?: T,
    trackingId?: string,
    forceNewVerificationRequest?: boolean,
  ) => {
    dispatch<IsLoadingAction>({ type: "IS_LOADING", isLoading: true });
    try {
      const verificationResponsePromise = VerificationApiClient.fetchNewVerificationRequest(
        programId,
        trackingId,
        forceNewVerificationRequest,
      );
      return createFetchVerificationRequest({
        dispatch,
        verificationResponsePromise,
        programId,
        segment,
        previousViewModel,
        // we always fetch a "new" verification request. And if there's an
        // existing verification ID that's hidden in the above promise. So we
        // check again and pass it through here if there is one. Which allows
        // this function to treat new and existing requests differently.
        // TODO: refactor ALL of this.
        verificationId:
          getOptions().verificationId || getVerificationIdFromQueryString(window.location.search),
      });
    } catch (e) {
      logger.error(e, "fetchNewVerificationRequest");
    }
  };

/**
 * @description Provided with a verificationId, continue verifying someone.
 * @param dispatch Redux action dispatch
 */
export const createFetchExistingVerificationRequest: BoundActionCreator =
  (dispatch: Dispatch): BoundAction =>
  async (
    programId: DatabaseId,
    verificationId: string,
    previousVerificationResponse?: VerificationResponse,
    previousViewModel?: ViewModel,
    needsLoading: boolean = true,
  ) => {
    if (needsLoading) dispatch<IsLoadingAction>({ type: "IS_LOADING", isLoading: true });

    try {
      const verificationResponsePromise =
        VerificationApiClient.fetchExistingVerificationRequest(verificationId);
      return createFetchVerificationRequest({
        dispatch,
        verificationResponsePromise,
        programId,
        previousViewModel,
        previousVerificationResponse,
        verificationId,
      });
    } catch (e) {
      logger.error(e, "fetchExistingVerificationRequest");
    }
  };

export const segmentChanged = (currentSegment: Segment, nextSegment: Segment): boolean => {
  if (!nextSegment) {
    return false;
  }
  return !!(currentSegment && currentSegment !== nextSegment);
};

export const currentStepChanged = (
  previousVerificationResponse: VerificationResponse,
  verificationResponse: VerificationResponse,
): boolean =>
  !previousVerificationResponse ||
  previousVerificationResponse.currentStep !== verificationResponse.currentStep;

/**
 * @description Await several items (new/existing verification request, program theme, etc) then update central state (redux).
 * Timing and order is very important within this function as it is bootstrapping most of the application.
 *
 * @param dispatch Redux action dispatch
 * @param verificationResponsePromise Either a new or existing verification server request.
 * @param programId
 * @param previousViewModel Optional
 * @private
 */
export const createFetchVerificationRequest = async ({
  dispatch,
  verificationResponsePromise,
  programId,
  segment,
  previousViewModel,
  previousVerificationResponse,
  verificationId,
}: {
  dispatch: Dispatch;
  programId: DatabaseId;
  verificationResponsePromise: Promise<VerificationResponse>;
  segment?: Segment;
  previousViewModel?: ViewModel;
  previousVerificationResponse?: VerificationResponse;
  verificationId?: string;
}): Promise<any> => {
  try {
    const fingerprintPromise: Promise<string> = getFingerprint();
    let verificationResponse: VerificationResponse;
    let programTheme: ProgramTheme;
    let locale: Locale;
    try {
      if (verificationId) {
        try {
          // when there's an existing verification ID we need to fetch the
          // response before the theme to ensure the correct locale.
          verificationResponse = await verificationResponsePromise;
          logger.info("verificationResponse received: ", verificationResponse);
          locale = getLocaleSafely(previousViewModel, verificationResponse);
        } finally {
          // fetch program theme data even if we fail to fetch an existing verification
          // response, partly to provide customCss to the error step.
          programTheme = await VerificationApiClient.fetchProgramTheme(programId, locale);
        }
      } else {
        // otherwise we can do them in parallel
        locale = getLocaleSafely(previousViewModel);
        [verificationResponse, programTheme] = await Promise.all([
          verificationResponsePromise,
          VerificationApiClient.fetchProgramTheme(programId, locale),
        ]);
        logger.info("verificationResponse received: ", verificationResponse);
      }
    } catch (error) {
      logger.error(error, "verificationResponse");
      verificationResponse = getMockVerificationRequestErrorResponse(error.message);
      locale = getLocaleSafely(previousViewModel, verificationResponse);
      programTheme = programTheme || GetEmptyTheme();
    }

    const nextSegment = verificationResponse.segment;
    const previousSegment = previousVerificationResponse?.segment || segment;
    const themeMessages: ProgramThemeMessages = getThemeMessages(programTheme);

    // Load messages resource bundle to i18next "theme" namespace. Move this to
    // i18next `resourcesToBackend` function definition, once moved to localization service
    i18next.addResourceBundle(locale, "theme", themeMessages, false, true);

    if (i18next.language !== locale) {
      logger.info(
        `createFetchVerificationRequest: change language from "${i18next.language}" to "${locale}"`,
      );
      await i18next.changeLanguage(locale, logLanguageChangeErrors);
    }

    const messagesPromise: Promise<StringMap> = getMessages(
      locale,
      themeMessages,
      verificationResponse.segment,
    );

    let updatedPreviousViewModel = clearUploadedFiles(previousViewModel as DocUploadViewModel); // Need to remove the file elements from state since deepClone cannot clone them

    if (segmentChanged(previousSegment, nextSegment)) {
      logger.info("segment changed: ", previousSegment, nextSegment);
      await doViewModelReset(dispatch)(verificationResponse);
      updatedPreviousViewModel = undefined;
    }

    const fingerprint = await fingerprintPromise;
    const { currentStep } = verificationResponse;
    const countries: Country[] = getConfiguredCountries(programTheme);
    const country: Country = determineCountry(verificationResponse, countries);
    let viewModel = initViewModel({
      currentStep,
      fingerprint,
      locale,
      country,
      previousViewModel: updatedPreviousViewModel,
      externalUserId: getQueryOrOption(QUERY_STRING_EXTERNAL_USER_ID),
    });
    viewModel = enhanceOrganizationFromTheme(viewModel, programTheme);

    if (currentStepChanged(previousVerificationResponse, verificationResponse)) {
      if (
        previousVerificationResponse &&
        previousVerificationResponse.currentStep === "sso" &&
        verificationResponse.currentStep !== "sso" &&
        verificationResponse.currentStep !== "pending"
      ) {
        // PROM-773 since we're no-longer on step sso (here in tab1), close the sso tab (tab2) we opened previously
        closeTabRef();
      }
    }

    saveVerificationIdForConversion(verificationResponse.verificationId);

    if (viewModel && viewModel.hasOwnProperty("metadata")) {
      (viewModel as PersonalInfoViewModel).metadata = {
        ...(viewModel as PersonalInfoViewModel).metadata,
        ...getMetadataFromUrl(getMetadataConfig(programTheme).keys),
      };
    }

    const messages: StringMap = await messagesPromise;

    setProgramBaseline(programTheme);

    return Promise.all([
      dispatch<VerificationResponseAction>({ verificationResponse, type: "VERIFICATION_RESPONSE" }),
      dispatch<ViewModelAction>({ viewModel, type: "VIEW_MODEL" }),
      dispatch<ProgramThemeAction>({ programTheme, type: "PROGRAM_THEME" }),
      dispatch<SetMessagesAction>({ messages, type: "SET_MESSAGES" }),
      dispatch<IsLoadingAction>({ type: "IS_LOADING", isLoading: false }),
      dispatch<RequiredMetadataAndExternalIdCheckAction>({
        type: "REQUIRED_METADATA_AND_EXTERNAL_ID_CHECK",
      }),
    ])
      .then(() => {
        hooks.emit("ON_VERIFICATION_READY", verificationResponse);
        // This hook is a special case
        // Do not call this hook again after the page has initially loaded, such as pending step's polling behavior
        hooks.off("ON_VERIFICATION_READY");
      })
      .catch((reason) => {
        logger.error(reason, "createFetchVerificationRequest promises");
      });
  } catch (e) {
    logger.error(e, "createFetchVerificationRequest");
    throw e;
  }
};

/**
 * @description Action creator to update the view model in redux.
 * @param dispatch
 */
export const createUpdateViewModel: BoundActionCreator =
  (dispatch: Dispatch): BoundAction =>
  async (viewModel: ViewModel, options: Partial<ViewModelUpdateOptions>) => {
    const { partial = false, metadataCheck = false, externalUserIdCheck = false } = options || {};
    const promises: VerificationServiceAction[] = [
      dispatch<ViewModelAction>({
        viewModel,
        type: "VIEW_MODEL",
        partial,
      }),
    ];
    if (metadataCheck || externalUserIdCheck) {
      promises.push(
        dispatch<RequiredMetadataAndExternalIdCheckAction>({
          type: "REQUIRED_METADATA_AND_EXTERNAL_ID_CHECK",
        }),
      );
    }

    return Promise.all(promises);
  };

type CreateUpdateProps = (dispatch: Dispatch, programId: DatabaseId) => BoundAction;
export const createUpdateLocale: CreateUpdateProps =
  (dispatch: Dispatch, programId): BoundAction =>
  async (locale: Locale, segment: Segment): Promise<object> => {
    dispatch<IsLoadingAction>({ type: "IS_LOADING", isLoading: true });
    const newProgramTheme: ProgramTheme = await VerificationApiClient.fetchProgramTheme(
      programId,
      locale,
    );
    const partialVm: Partial<WithLocaleChoiceAndCountryChoice> = {
      localeChoice: { value: locale, label: locale },
    };
    setOptions({ locale });

    const themeMessages: ProgramThemeMessages = getThemeMessages(newProgramTheme);

    // Load messages resource bundle to i18next "theme" namespace. Move this to
    // i18n `resourcesToBackend` function definition, once moved to localization service
    i18next.addResourceBundle(locale, "theme", themeMessages, false, true);

    await i18next.changeLanguage(locale, logLanguageChangeErrors);

    hooks.emit("ON_FORM_LOCALE_CHANGE", locale);
    return getMessages(locale, themeMessages, segment).then((messages: StringMap) =>
      Promise.all([
        dispatch<SetMessagesAction>({ messages, type: "SET_MESSAGES" }),
        dispatch<ViewModelAction>({ viewModel: partialVm, type: "VIEW_MODEL", partial: true }),
        dispatch<IsLoadingAction>({ type: "IS_LOADING", isLoading: false }),
      ]),
    );
  };

/**
 * @description Action creator to update the program theme in redux.
 * @param dispatch
 */
export const createUpdateProgramTheme: BoundActionCreator =
  (dispatch: Dispatch): BoundAction =>
  async (programTheme: ProgramTheme, partial: boolean = false) => {
    const themeMessages = getThemeMessages(programTheme);
    if (programTheme.intl && programTheme.intl.locale && programTheme.intl.messages) {
      i18next.addResourceBundle(programTheme.intl.locale, "theme", themeMessages, false, true);
    }
    return dispatch<ProgramThemeAction>({ programTheme, type: "PROGRAM_THEME", partial });
  };

/**
 * @description Perform a full reset of the in-memory state
 * Useful to fully reset the form and start over again, similar to a page refresh.
 */
export const createResetState =
  (dispatch: Dispatch): BoundAction =>
  async () =>
    dispatch({ type: "RESET_STATE" });

/**
 * @description Action creator to update the field validation errors, which are those that are shown to a user as they fill-in the HTML form.
 * @param dispatch
 */
export const createUpdateFieldValidationErrors: BoundActionCreator =
  (dispatch: Dispatch): BoundAction =>
  async (fieldValidationErrors: FieldValidationErrors) =>
    dispatch<FieldValidationErrorsAction>({
      type: "FIELD_VALIDATION_ERRORS",
      fieldValidationErrors,
    });

/**
 * @description Action creator to submit a step to the backend REST API
 * @param dispatch
 */
type CreateSubmitStepProps = (dispatch: Dispatch, programTheme: ProgramTheme) => BoundAction;
export const createSubmitStep: CreateSubmitStepProps =
  (dispatch: Dispatch, programTheme: ProgramTheme): BoundAction =>
  async (
    stepName: VerificationStep,
    viewModel: ViewModel,
    previousResponse: VerificationResponse,
  ) => {
    logger.log(`submitStep(): submitting stepName ${stepName}, viewModel:`, viewModel);
    scrollToWindowTop();
    dispatch<IsLoadingAction>({ type: "IS_LOADING", isLoading: true, loadingStep: stepName });
    logger.info(`submitting step ${stepName}`, viewModel);

    setGaDimensionTestRequest(viewModel as WithCoreFields);

    let submittedViewModel = viewModel;
    const fpSessionId = getFingerprintRequestId();
    const ipqsRequestId = getIpqsRequestId();
    if (isCollectStep(stepName) && (fpSessionId || ipqsRequestId)) {
      submittedViewModel = produce(viewModel, (draft) => {
        if (!draft.metadata) {
          draft.metadata = {};
        }
        if (fpSessionId) {
          draft.metadata.fpSessionId = fpSessionId;
        }
        if (ipqsRequestId) {
          draft.metadata.ipqsRequestId = ipqsRequestId;
        }
      });
    }

    const verificationResponse: VerificationResponse = await VerificationApiClient.submitStep(
      stepName,
      previousResponse,
      submittedViewModel,
    );
    const newViewModel = handleSubmitResponse(
      verificationResponse,
      submittedViewModel,
      programTheme,
    );

    const promiseVerificationResponse = dispatch<VerificationResponseAction>({
      verificationResponse,
      type: "VERIFICATION_RESPONSE",
    });
    const promiseViewModel = dispatch<ViewModelAction>({
      viewModel: newViewModel,
      type: "VIEW_MODEL",
    });

    if (
      isCollectStep(verificationResponse.currentStep) &&
      verificationResponse?.errorIds?.length !== 0
    ) {
      const fieldValidationErrors: FieldValidationErrors = getServerFieldValidationErrors(
        verificationResponse.errorIds,
      );

      dispatch<FieldValidationErrorsAction>({
        type: "FIELD_VALIDATION_ERRORS",
        fieldValidationErrors,
      });
    }

    const promiseIsLoading = dispatch<IsLoadingAction>({ type: "IS_LOADING", isLoading: false });

    return Promise.all([promiseVerificationResponse, promiseViewModel, promiseIsLoading]);
  };

/**
 * Perform the actual viewModel reset actions
 * @private
 */
export const doViewModelReset =
  (dispatch: Dispatch) =>
  (verificationResponse: VerificationResponse): Promise<any> =>
    Promise.all([
      dispatch<ViewModelAction>({
        type: "VIEW_MODEL",
        viewModel: getEmptyViewModel(verificationResponse.currentStep),
      }),
      dispatch<PreviousViewModelAction>({
        type: "PREVIOUS_VIEW_MODEL",
        previousViewModel: undefined,
      }),
      dispatch<FieldValidationErrorsAction>({
        type: "FIELD_VALIDATION_ERRORS",
        fieldValidationErrors: getFieldValidationErrorsEmpty(),
      }),
    ]);
